import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { Tooltip } from '@yola/ws-ui';
import { customization, dialogs, globals, i18next, template } from '@yola/ws-sdk';
import { useSelector, useDispatch } from 'react-redux';
import { compose } from 'redux';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import {
  DIALOG_BACKGROUND_TRIGGER_ID,
  SUBMIT_BUTTON_TRIGGER_ID,
} from 'src/js/modules/dialogs/constants/common';
import segment from 'src/js/modules/analytics/segment';
import helpers from '../helpers';
import actions from '../actions';
import triggerIds from '../constants/trigger-ids';
import constants from '../constants';
import readOnlyModeDecorator from '../helpers/read-only-mode-decorator';
import DesignColorsDialog from '../components/design-colors-dialog';

const { createMapById, prepareNodeElement, setCurrentPalette, getSelectorByGlobalId, sortPresets } =
  helpers;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const { GLOBAL_HEADER_CODE } = constants;

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Colors')],
  title: i18next.t('Choose a color palette'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t(
    'Switch or edit color presets to instantly change the overall appearance of your site'
  ),
  heading: i18next.t('Library'),
  customPalettesTitle: i18next.t('Custom palettes'),
  paletteLabel: i18next.t('Palette'),
  customPaletteLabel: i18next.t('Custom palette'),
  editLabel: i18next.t('Settings'),
  deleteLabel: i18next.t('Delete'),
});

const DesignColorsContainer = ({
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
  sourceId,
}) => {
  const {
    updatedPalettes,
    initialColorPaletteId: sharedInitialColorPaletteId,
    activeColorPaletteId: sharedActiveColorPaletteId,
    offsetX,
    offsetY,
  } = getSharedData();

  const colorPalettes = useSelector(customization.selectors.getColorPalettes);
  const initialCustomColorPalettes = useSelector(customization.selectors.getCustomColorPalettes);
  const customPaletteTemplate = useSelector(customization.selectors.getColorPaletteTemplate);
  const currentColorPalette = useSelector(customization.accessors.getCurrentColorPalette);
  const colorPaletteVariable = useSelector((state) =>
    template.selectors.getVariableValueByPath(state, customization.constants.COLOR_PALETTE_VARIABLE)
  );

  const initialColorPaletteId = sharedInitialColorPaletteId || currentColorPalette?.id;

  const [dialogPosition, updatePosition] = useState({
    offsetX,
    offsetY,
  });

  const [activeColorPaletteId, setActiveColorPaletteId] = useState(
    sharedActiveColorPaletteId || initialColorPaletteId
  );

  const [customColorPalettes, setCustomColorPalettes] = useState(
    sortPresets(updatedPalettes || initialCustomColorPalettes || [], initialColorPaletteId)
  );

  const defaultColorPalettes = useMemo(
    () => sortPresets(colorPalettes, initialColorPaletteId),
    [colorPalettes, initialColorPaletteId]
  );

  const dispatch = useDispatch();

  const palettesMap = useMemo(
    () =>
      createMapById([
        ...defaultColorPalettes,
        ...(updatedPalettes || initialCustomColorPalettes || []),
      ]),
    [defaultColorPalettes, updatedPalettes, initialCustomColorPalettes]
  );

  const changeColorPalette = (isChanged) => dispatch(actions.changeColorPalette(isChanged));

  const applyColorPalette = (palette) => {
    const { element, paletteElement: colorPaletteElement } = prepareNodeElement({
      elementVariable: colorPaletteVariable,
      customTemplate: customPaletteTemplate,
      currentPalette: palette,
      elementDataAttribute: customization.constants.COLOR_PALETTE_DATA_ATTRIBUTE,
    });

    if (element) {
      element.parentNode.replaceChild(colorPaletteElement, element);
    }
  };

  const updateSharedState = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      customColorPalettes,
      ...customData,
    });
  };

  const updateCustomPalettes = () => {
    customization.helpers
      .getCustomPalettesToDelete(initialCustomColorPalettes, customColorPalettes)
      .forEach((id) => dispatch(customization.actions.deleteCustomColorPalette(id)));
    customization.helpers
      .getCustomPalettesToCreate(initialCustomColorPalettes, customColorPalettes)
      .forEach((palette) => dispatch(customization.actions.addCustomColorPalette(palette)));
    customization.helpers
      .getCustomPalettesToUpdate(initialCustomColorPalettes, customColorPalettes)
      .forEach((palette) => dispatch(customization.actions.updateCustomColorPalette(palette)));
  };

  const handleEditPaletteClick = (id, isCustom) => {
    const palette = palettesMap[id];

    updateSharedState({
      originPalette: palette,
      activePalette: palettesMap[activeColorPaletteId],
      isEdit: isCustom,
    });

    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE, {
        triggerId: triggerIds.PALETTE_SETTINGS,
        colorPreset: palette.id,
      })
    );
  };

  const handleAddPaletteClick = () => {
    const palette = palettesMap[activeColorPaletteId];

    updateSharedState({
      originPalette: palette,
      activePalette: palette,
      isEdit: false,
    });

    dispatch(
      dialogs.actions.show(dialogTypes.BASE_COLORS_PICKER_DIALOG, {
        triggerId: triggerIds.ADD_CUSTOM_PALETTE,
        colorPreset: palette.id,
      })
    );
  };

  const handleChangeColorPalette = (colorPaletteId) => {
    applyColorPalette(palettesMap[colorPaletteId]);
    setActiveColorPaletteId(colorPaletteId);

    if (activeColorPaletteId !== colorPaletteId) {
      dispatch(actions.switchColorPalette(palettesMap[colorPaletteId]));
    }

    trackEvent(events.DESIGN_SETTINGS_COLOR_PRESET_CHANGED, {
      dialogId: dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG,
      colorPreset: colorPaletteId,
    });
  };

  const handleDeletePaletteClick = (id) => {
    const filteredColorPalettes = customColorPalettes.filter((palette) => palette.id !== id);

    Tooltip.hide();

    trackEvent(events.DESIGN_SETTINGS_COLOR_PRESET_DELETED, {
      dialogId: dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG,
    });

    if (activeColorPaletteId === id) {
      handleChangeColorPalette(defaultColorPalettes[0].id);
    }

    setCustomColorPalettes(filteredColorPalettes);
  };

  const onClose = () => {
    dialogs.operations.hide();
  };

  const isPaletteChanged = () => {
    const { originPalette } = getSharedData();

    return originPalette
      ? !isEqual(originPalette, palettesMap[activeColorPaletteId])
      : initialColorPaletteId !== activeColorPaletteId;
  };

  const handleCancel = () => {
    if (isPaletteChanged()) {
      const initialPalettesMap = createMapById([
        ...defaultColorPalettes,
        ...(initialCustomColorPalettes || []),
      ]);

      applyColorPalette(initialPalettesMap[initialColorPaletteId]);
      changeColorPalette(false);
    }

    onDialogCancel({
      sourceId,
    });
    onClose();
  };

  const handleSubmit = (event) => {
    if (customColorPalettes) {
      updateCustomPalettes();
    }

    const isPaletteEdited = isPaletteChanged();

    if (isPaletteEdited) {
      globals.operations.changeGlobalContent(
        GLOBAL_HEADER_CODE,
        (global) => {
          const transformations = [];

          const palette = palettesMap[activeColorPaletteId];
          const colorSelector = getSelectorByGlobalId(colorPaletteVariable, GLOBAL_HEADER_CODE);
          const setCurrentPaletteCurry = (globalItem) =>
            setCurrentPalette(globalItem, palette, customPaletteTemplate, colorSelector);
          transformations.push(setCurrentPaletteCurry);

          return compose(...transformations)(global);
        },
        false
      );

      changeColorPalette(true);
    }

    onDialogMainAction({
      triggerId: !event ? DIALOG_BACKGROUND_TRIGGER_ID : SUBMIT_BUTTON_TRIGGER_ID,
      sourceId,
      colorPaletteAdjusted: isPaletteEdited,
      ...(isPaletteEdited && { colorPreset: activeColorPaletteId }),
    });
    onClose();
  };

  const handleDragEnd = (_, { x, y }) => {
    updatePosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const captions = getCaptions();

  return (
    <DesignColorsDialog
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      activePaletteId={activeColorPaletteId}
      palettes={defaultColorPalettes}
      setActivePalette={readOnlyModeDecorator(handleChangeColorPalette)}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
      {...(customColorPalettes && {
        customPalettes: customColorPalettes,
        onAddPaletteClick: readOnlyModeDecorator(handleAddPaletteClick),
        onEditPaletteClick: handleEditPaletteClick,
        onDeletePaletteClick: readOnlyModeDecorator(handleDeletePaletteClick),
      })}
    />
  );
};

DesignColorsContainer.propTypes = {
  sourceId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default DesignColorsContainer;
